import {
  MESSAGES_PER_PAGE,
  pageNumberInfinityModel,
  pageNumberInfinityOptions,
} from '../utils/messaging/infinity-model';
import { MODEL_NAMES } from '../constants/index';
import { A as emberArray } from '@ember/array';
import { getInactiveReasonsFromResponse } from '../utils/messaging/conversation';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import Service, { service } from '@ember/service';

export default class MessagingConversationService extends Service {
  @service alertManager;
  @service infinity;
  @service intl;
  @service messaging;
  @service store;

  @tracked inactiveReasons = [];
  @tracked messages = emberArray();
  @tracked selectedConversation;

  async openConversationPage(conversation) {
    try {
      this.selectedConversation = conversation;

      if (conversation.isNew || conversation.inactive) {
        const response = await this.fetchAndValidateUsers.perform(conversation);
        if (response.ok) {
          await this.#handleUserFetchSuccess(response, conversation);
          await this.saveConversation(conversation);
        } else {
          await this.#handleUserFetchError(response);
        }
      }
      this.getMessages.perform();
    } catch (e) {
      this.closeConversationPage();
      this.#handleConversationError();
    }
  }

  closeConversationPage() {
    this.selectedConversation = undefined;
    this.messages = [];
    this.inactiveReasons = [];
  }

  getMessages = task({ drop: true }, async () => {
    if (this.selectedConversation.isNew) {
      return;
    }

    this.messages = await this.initMessagesInfinityModel.perform();
  });

  loadMoreMessages = task({ drop: true }, async model => {
    try {
      await this.infinity.loadNextPage(model);
    } catch (err) {
      this.#handleConversationError();
    }
  });

  fetchAndValidateUsers = task({ drop: true }, async conversation => {
    return this.store.adapterFor(MODEL_NAMES.CONVERSATION).fetchAndValidateUsers(conversation);
  });

  saveNewConversation = task({ drop: true }, async (conversation, firstMessage) => {
    if (!conversation.isNew) {
      return;
    }

    await this.saveConversation(conversation);
    const infinityModel = await this.initMessagesInfinityModel.perform();
    infinityModel.unshiftObject(firstMessage);
    this.messages = infinityModel;
  });

  // This is a workaround to avoid a bug between ember-concurrency and ember-data. Trying to await a
  // save inside of a task can cause an error around `__ec_cancel` and PromiseProxy deprecation
  // so we move it outside the task.
  async saveConversation(conversation) {
    await conversation.save();
  }

  initMessagesInfinityModel = task({ drop: true }, async () => {
    return await this.infinity.model(
      MODEL_NAMES.MESSAGE,
      {
        conversationId: this.selectedConversation.id,
        include: 'conversation,user',
        filter: {
          'include_system': this.messaging.currentProfile.isClinician,
        },
        ...pageNumberInfinityOptions(MESSAGES_PER_PAGE),
      },
      pageNumberInfinityModel(MESSAGES_PER_PAGE)
    );
  });

  async #handleUserFetchSuccess(response, conversation) {
    this.inactiveReasons = [];
    const json = await response.json();
    await this.#updateUserRecords(json, conversation);
  }

  async #updateUserRecords(json) {
    const unsavedUsers = this.store.peekAll(MODEL_NAMES.USER).filterBy('isNew');

    for (const jsonUser of json.data) {
      const normalizedUser = this.store.normalize(MODEL_NAMES.USER, jsonUser);
      const { externalId } = normalizedUser.data.attributes;

      const matchingUnsavedUser = unsavedUsers.find(user => user.externalId === externalId);

      if (matchingUnsavedUser) {
        // Since the user was already created in the backend, we pass the data as an adapter option
        await matchingUnsavedUser.save({ adapterOptions: { data: jsonUser } });
      } else {
        this.store.push(normalizedUser);
      }
    }
  }

  async #handleUserFetchError(response) {
    if (response.status !== 422) {
      throw new Error(this.intl.t('emc.general_error'));
    }

    this.inactiveReasons = await getInactiveReasonsFromResponse(response);
  }

  #handleConversationError() {
    this.alertManager.broadcast('toast', {
      message: this.intl.t('emc.general_error'),
      type: 'error',
    });
  }
}
